import React, {
  useContext,
  useState,
  useImperativeHandle,
  useCallback,
  useRef,
  useEffect,
  ReactComponentElement,
  createRef,
} from "react";
import type { MouseEvent } from "react";
import viewerContext from "../utils/viewerContext";
import { MutableRefObject } from "react";


/**
 *
 * @param {Node} parent 表示container的DOM对象
 * @param {Node} self 表示小圆球的DOM对象
 * @param {MouseEvent} e 表示鼠标的对象
 * @returns Object
 */
const calculateStyle = (
  parent: HTMLDivElement | null,
  self: HTMLDivElement | null,
  e?: MouseEvent
) => {
  if (!parent || !self) return {};

  // 获取当前对象的宽高的一半
  const sliderW2 = self.clientWidth / 2;
  const sliderH2 = self.clientHeight / 2;

  // 还需要加上border
  const borderWidth = window.getComputedStyle(parent).borderWidth;
  // 去掉px再除以2
  const borderW2 = Number(borderWidth.substring(0, borderWidth.length - 2)) / 2;

  // radius半径应该是parentRef的半径
  // 这里用的是clientWidth，另一个选择是offsetWidth
  const radius = parent.clientWidth / 2;

  // elPos，指的是parent的位置
  const parentRects = parent.getBoundingClientRect();
  const elPos = { x: parentRects.x, y: parentRects.y };

  // center
  const centerOfCircle = { x: radius, y: radius };

  // 相当于mousemove的逻辑
  // 这里模拟一下e不存在的情况
  let mPos;
  if (e) {
    mPos = { x: e.clientX - elPos.x, y: e.clientY - elPos.y };
  } else {
    // 此时e不存在，归零的操作
    mPos = { x: centerOfCircle.x + 1, y: centerOfCircle.y };
  }
  const atan = Math.atan2(mPos.x - centerOfCircle.x, mPos.y - centerOfCircle.y);
  const deg = -atan / (Math.PI / 180) + 180; // final (0-360 positive) degrees from mouse position

  const X = Math.round(radius * Math.sin((deg * Math.PI) / 180));
  const Y = Math.round(radius * -Math.cos((deg * Math.PI) / 180));

  const offset = {
    left: X + radius - sliderW2,
    top: Y + radius - sliderH2,
  };

  const translate = `rotate(${deg}deg)`;

  const newStyle = {
    // 位置
    left: offset.left,
    top: offset.top,
    // 旋转
    transform: translate,
  };

  return { newStyle, deg };
};

interface Methods {
  changeLittlePlace: (e: MouseEvent) => void;
  resetStyle: () => void;
}

interface LittleCircleProps {
  parentRef: React.RefObject<HTMLDivElement>;
  methodsRef: React.RefObject<Methods>;
}

// 学习 http://jsfiddle.net/phdphil/Zv4K7/#base 这个项目
const LittleCircle = React.forwardRef<HTMLDivElement, LittleCircleProps>(
  ({ parentRef, methodsRef }, selfRef) => {

    const [selfStyle, setSelfStyle] = useState(
      // 因为真正到调用的时候selfRef肯定不是null
      () => {
        // 这一句cast才能平息ts
        selfRef = selfRef as MutableRefObject<HTMLDivElement>
        if(!selfRef.current){
          return;
        }
        return calculateStyle(parentRef.current, selfRef.current).newStyle || {} 
      }
    );

    const { setRotationDegree } = useContext(viewerContext);

    /**
     * 将小圆点的位置进行修改
     */
    const changeLittlePlace = useCallback(
      (e: MouseEvent) => {
        selfRef = selfRef as MutableRefObject<HTMLDivElement>
        if (!selfRef || !selfRef.current || !parentRef.current) return;
        const parent = parentRef.current;
        const self = selfRef.current;

        const { newStyle, deg } = calculateStyle(parent, self, e);

        if (newStyle && deg) {
          // 修改style
          setSelfStyle(newStyle);

          // deg是以12点方向为0的，而我们要的是以3点方向为0
          console.log(deg);

          const deg2 = Math.round(deg) - 90;
          const deg3 = deg2 >= 0 ? deg2 : 360 + deg2;
          setRotationDegree(deg3);
        }
      },
      [parentRef, selfRef, setRotationDegree]
    );

    useEffect(() => resetStyle(), []);

    /**
     * 将小圆点的style修改为0值
     */
    const resetStyle = () => {
      selfRef = selfRef as MutableRefObject<HTMLDivElement>
      const initialStyle =
        calculateStyle(parentRef.current, selfRef.current).newStyle || {};
      setSelfStyle(initialStyle);
    };

    /**
     * 定义两个函数，方便外部调用
     */
    useImperativeHandle(
      methodsRef,
      () => {
        return {
          changeLittlePlace,
          resetStyle,
        };
      },
      [selfRef, parentRef]
    );

    return (
      <div
        className="LittleCircle absolute w-3 h-3 bg-red-400 bg-opacity-75 rounded-full hover:bg-blue-100"
        ref={selfRef}
        style={selfStyle}
      />
    );
  }
);

let mdown = false;

const RotationCircle: React.FC = () => {
  const { setRotationDegree, rotationDegree } = useContext(viewerContext);

  /**
   * 当点击时，将degree变成0，与indirectHandleClick互相对照，
   * 使用useCallback可以避免循环调用
   */
  const handleClick = useCallback(() => {
    setRotationDegree(0);
    methodsRef.current && methodsRef.current.resetStyle();
  }, [setRotationDegree]);

  /**
   * 这个函数在jsx里面调用的时候，一定要用() => indirectHandleClick()这种方式，
   * 否则会产生循环调用。
   *
   * 另一种方式，是使用useCallback + setRotationDegree
   * TODO: 当我们完全不怕循环调用的时候，可以在代码中只保留一种
   */
  const indirectHandleClick = () => {
    setRotationDegree(0);
    methodsRef.current && methodsRef.current.resetStyle();
  };

  // container
  // 写Typescript的时候要用createRef来替代useRef，否则会比较麻烦
  const divRef = createRef<HTMLDivElement>();
  // slider
  const littleRef = createRef<HTMLDivElement>();
  // slider methods
  const methodsRef = createRef<Methods>();

  const handleMouseMove = (e: MouseEvent) => {
    if (mdown) {
      methodsRef.current && methodsRef.current.changeLittlePlace(e);
    }
  };

  return (
    <>
      <div
        className="RotationCircle relative opacity-75 grid w-40 h-40 bg-gray-300 bg-opacity-0 border-8 rounded-full border-red-100"
        ref={divRef}
        onMouseDown={() => {
          mdown = true;
        }}
        onMouseMove={(e) => {
          handleMouseMove(e);
        }}
        onMouseUp={(e) => {
          handleMouseMove(e);
          mdown = false;
        }}
        onTouchMove={(e) => {
          // 这里typescript会报错，但是我们这里允许这种行为
          handleMouseMove(e as any as MouseEvent);
        }}
      >
        <p
          onClick={() => indirectHandleClick()}
          className="absolute place-self-center opacity-75 text-3xl font-light leading-10 text-center text-red-50"
        >
          {rotationDegree}
        </p>
        <LittleCircle
          parentRef={divRef}
          ref={littleRef}
          methodsRef={methodsRef}
        />
      </div>
    </>
  );
};

export default RotationCircle;
